/**
 * Public/private switch for one artist_media row (private media library).
 *
 * The public copies of an item are the R2 objects served by cdn.ballee.co:
 * `r2_key` and the two SEO derivatives. The Supabase Storage `dancer-media`
 * object is the private half and stays. `DancerMediaService.setShownOnProfile`
 * and `deleteMedia` delegate here so the key cleanup has one home.
 *
 * Falsifier: an R2 delete must never remove bytes that have no Supabase
 * original. Rows whose only bytes live on R2 (an absolute CDN storage_path, or
 * a Mux ingest source with no storage_path) are copied into `dancer-media`
 * first; R2 is cleaned only after that copy and the row update both succeeded.
 *
 * WIP: docs/wip/active/WIP_building_private_media_library_2026_10_24.md
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';
import { StorageBuckets, type R2Service } from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

type MediaRow = Database['public']['Tables']['artist_media']['Row'];

/** Key prefix of the public R2 copy of a Supabase original. */
export const R2_ORIGINAL_PREFIX = 'artist-media';

const IMAGE_MEDIA_TYPES = new Set(['headshot', 'photo', 'cover']);

const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  jpe: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};

function extensionOf(name: string | null | undefined): string {
  const ext = name?.split('?')[0]?.split('.').pop()?.toLowerCase() ?? '';
  return /^[a-z0-9]{1,5}$/.test(ext) ? ext : '';
}

/** R2 key behind an absolute CDN storage_path, or null for a bucket path. */
function r2KeyOfAbsolutePath(
  storagePath: string | null,
  r2: R2Service,
): string | null {
  if (!storagePath) return null;
  const base = r2.getPublicUrl('');
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return storagePath.startsWith(prefix)
    ? storagePath.slice(prefix.length)
    : null;
}

/**
 * Every R2 object that makes the row publicly reachable: the stamped keys and
 * an original that lives on R2 only. De-duplicated, in a stable order.
 */
export function publicR2Keys(row: MediaRow, getR2: () => R2Service): string[] {
  return [
    ...new Set(
      [
        row.r2_key,
        row.seo_poster_r2_key,
        row.seo_video_r2_key,
        row.storage_path?.startsWith('http')
          ? r2KeyOfAbsolutePath(row.storage_path, getR2())
          : null,
      ].filter((key): key is string => Boolean(key)),
    ),
  ];
}

const toError = (message: string, cause?: unknown) =>
  Result.fail<void>(ServiceError.internal(message, cause));

/**
 * Make sure the row has a Supabase original. Returns the storage_path to keep,
 * or a failure when the bytes cannot be secured (the caller then leaves R2
 * untouched).
 *
 * A row with no storage_path and no R2 source (a Mux-only video) has only its
 * SEO derivatives as public copies. Those are renditions of what Mux holds, so
 * nothing needs securing: the result is `null` and the derivatives go.
 */
async function secureOriginal(
  client: SupabaseClient<Database>,
  r2: R2Service,
  row: MediaRow,
): Promise<Result<string | null>> {
  const bucket = client.storage.from(StorageBuckets.DANCER_MEDIA);
  const r2Source =
    r2KeyOfAbsolutePath(row.storage_path, r2) ??
    (!row.storage_path ? row.r2_key : null);

  if (!r2Source) return Result.ok(row.storage_path);

  const bytes = await r2.get(r2Source);
  if (bytes.isFailure) {
    return Result.fail(bytes.getError());
  }
  const ext = extensionOf(row.file_name) || extensionOf(r2Source) || 'bin';
  const path = `${row.user_id}/${row.media_type}/${row.id}.${ext}`;
  const { error } = await bucket.upload(path, bytes.getValue(), {
    contentType:
      row.mime_type ?? MIME_BY_EXT[ext] ?? 'application/octet-stream',
    upsert: true,
  });
  if (error) {
    return Result.fail(
      ServiceError.internal(`Copy to private storage failed: ${error.message}`),
    );
  }
  return Result.ok(path);
}

/** Remove the public R2 copies of a row. Never throws. */
export async function deletePublicCopies(
  r2: R2Service,
  keys: string[],
): Promise<Result<void>> {
  if (keys.length === 0) return Result.ok(undefined);
  const res = await r2.deleteObjects(keys);
  if (res.isFailure) return Result.fail(res.getError());
  const { failedKeys } = res.getValue();
  return failedKeys.length > 0
    ? toError(`R2 delete failed for ${failedKeys.length} object(s)`)
    : Result.ok(undefined);
}

export async function setRowShownOnProfile(
  client: SupabaseClient<Database>,
  getR2: () => R2Service,
  row: MediaRow,
  shown: boolean,
): Promise<Result<void>> {
  const table = () => client.from('artist_media');

  if (!shown) {
    // Hide first: the item is private even if everything below fails.
    const hide = await table()
      .update({ shown_on_profile: false })
      .eq('id', row.id);
    if (hide.error) {
      return Result.fail(ServiceError.database(hide.error.message, hide.error));
    }

    // Mux is still fetching the R2 source: deleting it now fails the ingest
    // and the video never plays. The row is already private; the Mux webhook
    // removes the public copies once the asset is ready or errored
    // (`DancerMediaService.removeDeferredPublicCopies`).
    if (row.media_type === 'video' && row.mux_status === 'preparing') {
      return Result.ok(undefined);
    }

    const keys = publicR2Keys(row, getR2);
    if (keys.length === 0) return Result.ok(undefined);
    const r2 = getR2();

    const original = await secureOriginal(client, r2, row);
    if (original.isFailure) return Result.fail(original.getError());

    const payload: Database['public']['Tables']['artist_media']['Update'] = {
      shown_on_profile: false,
      r2_key: null,
      seo_poster_r2_key: null,
      seo_video_r2_key: null,
    };
    if (original.getValue() !== row.storage_path) {
      payload.storage_path = original.getValue();
    }
    const stamp = await table().update(payload).eq('id', row.id);
    if (stamp.error) {
      return Result.fail(
        ServiceError.database(stamp.error.message, stamp.error),
      );
    }

    return deletePublicCopies(r2, keys);
  }

  // Showing again. Videos are served by Mux; only images get an R2 copy.
  const isImage =
    IMAGE_MEDIA_TYPES.has(row.media_type) &&
    Boolean(row.storage_path) &&
    !row.storage_path?.startsWith('http');
  const payload: Database['public']['Tables']['artist_media']['Update'] = {
    shown_on_profile: true,
  };

  if (isImage && !row.r2_key && row.storage_path) {
    const r2Key = `${R2_ORIGINAL_PREFIX}/${row.storage_path}`;
    const { data: blob, error } = await client.storage
      .from(StorageBuckets.DANCER_MEDIA)
      .download(row.storage_path);
    if (error || !blob) {
      return toError(
        `Private original unreadable: ${error?.message ?? 'empty'}`,
      );
    }
    const put = await getR2().upload(
      r2Key,
      Buffer.from(await blob.arrayBuffer()),
      {
        contentType:
          row.mime_type ??
          MIME_BY_EXT[extensionOf(row.storage_path)] ??
          'application/octet-stream',
      },
    );
    // A missing R2 copy only costs the CDN transform: the resolver falls back
    // to the Supabase original, so the item is still shown.
    if (put.isSuccess) payload.r2_key = r2Key;
  }

  const show = await table().update(payload).eq('id', row.id);
  return show.error
    ? Result.fail(ServiceError.database(show.error.message, show.error))
    : Result.ok(undefined);
}

/**
 * A re-upload can land on an existing row (the BAL-700 duplicate guard, or the
 * finalize RPC's ON CONFLICT). An upload from a surface that shows its items
 * (the profile editor, where the flag is omitted or true) shows that row when
 * it is private (R4). It never hides one: attaching a shown photo to an
 * application keeps it on the profile.
 */
export async function showIfUploadedShown(
  client: SupabaseClient<Database>,
  getR2: () => R2Service,
  row: MediaRow,
  shownOnProfile: boolean | undefined,
): Promise<Result<MediaRow>> {
  if (shownOnProfile === false || row.shown_on_profile !== false) {
    return Result.ok(row);
  }
  const shown = await setRowShownOnProfile(client, getR2, row, true);
  if (shown.isFailure) return Result.fail(shown.getError());
  return Result.ok({ ...row, shown_on_profile: true });
}
